import useSWR from "swr";
import { jwtDecode } from "jwt-decode";
import { SessionData, defaultSession } from "@/lib/session";
import useSWRMutation from "swr/mutation";
import { useRouter } from "next/navigation";
import { sessionStorageCleanup } from "@/utils/helper";

const sessionApiRoute = "/session";
const cpqLoginApiRoute = "/session/cpq-route";

interface LoginParams {
  email: string;
  password: string;
}
interface Token {
  token: string;
}

async function fetchJson<JSON = unknown>(
  input: RequestInfo,
  init?: RequestInit
): Promise<JSON> {
  return fetch(input, {
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    ...init,
  })
    .then((res) => res.json())
    .catch((error) => {
      throw new Error("Something went wrong!");
    });
}

function postRequest(url: string, { arg }: { arg: LoginParams | Token }) {
  return fetchJson<any>(url, {
    method: "POST",
    body: JSON.stringify({ ...arg }),
  });
}

function doLogout(url: string) {
  sessionStorageCleanup("logoutCleanup");
  return fetchJson<SessionData>(url, {
    method: "DELETE",
  });
}

function expire(url: string) {
  return fetchJson<SessionData>(url, {
    method: "GET",
  });
}

function doCpqLogin(url: string) {
  return fetchJson<{
    success: boolean;
    message?: string;
    error?: string;
  }>(url, {
    method: "POST",
  });
}

function checkTokenExpiry(session: SessionData, logout: () => void) {
  if (session?.token) {
    const decodedToken = jwtDecode<{ iat: number }>(session.token);
    const issuedAt = decodedToken.iat;
    const currentTime = Math.floor(Date.now() / 1000);
    const hoursSinceIssued = (currentTime - issuedAt) / 3600;

    if (hoursSinceIssued > 24) {
      logout();
      return true;
    }
  }
  return false;
}

export default function useSession() {
  const { data: session, isLoading } = useSWR(
    sessionApiRoute,
    fetchJson<SessionData>,
    {
      fallbackData: defaultSession,
    }
  );

  const { trigger: login } = useSWRMutation(sessionApiRoute, postRequest, {
    // the login route already provides the updated information, no need to revalidate
    revalidate: false,
  });

  const { trigger: logout } = useSWRMutation(sessionApiRoute, doLogout);

  const router = useRouter();
  if (checkTokenExpiry(session, logout)) {
    router.replace("/login");
  }

  const { trigger: getCustomerDetails } = useSWRMutation(
    sessionApiRoute + "/get-customer-details",
    postRequest
  );

  const { trigger: isExpired } = useSWRMutation(
    sessionApiRoute + "/is-expired",
    expire
  );

  const { trigger: getCpqLogin } = useSWRMutation(cpqLoginApiRoute, doCpqLogin);

  return {
    session,
    logout,
    login,
    getCustomerDetails,
    isLoading,
    getCpqLogin,
    isExpired,
  };
}
